import { useMemo, useState } from "react";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Alert,
  LinearProgress,
  Paper,
  Stack,
  Button,
  Chip,
  Avatar,
  Tabs,
  Tab,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  ButtonGroup,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { marketDataApi } from "../services/marketDataApi";
import { recommendationsApi } from "../services/recommendationsApi";
import { RecommendationSections } from "../components/recommendations/RecommendationSections";
import { tradingApi } from "../services/tradingApi";
import CommonDataGrid from "../components/grid/CommonDataGrid";
import SectionHeader from "../components/common/SectionHeader";
import OrderModal from "../components/trading/OrderModal";
import AccentCard from "../components/common/AccentCard";

const money = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 8,
  }).format(Number(value || 0));

const compactMoney = (value) => {
  if (value == null) return "—";
  if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(2)}K`;
  return money(value);
};

const pct = (value) => `${Number(value || 0).toFixed(2)}%`;
const signedMoney = (value) => `${value >= 0 ? "+" : "-"}${money(Math.abs(value))}`;

const chartRanges = [
  { value: "1d", label: "1D" },
  { value: "1w", label: "1W" },
  { value: "1m", label: "1M" },
  { value: "3m", label: "3M" },
];

const downsampleSeries = (points, maxPoints = 140) => {
  if (!points || points.length <= maxPoints) return points || [];
  const step = Math.ceil(points.length / maxPoints);
  return points.filter((_, index) => index % step === 0 || index === points.length - 1);
};

const AssetDetailPage = () => {
  const { marketAssetId } = useParams();
  const navigate = useNavigate();
  const [section, setSection] = useState(0);
  const [chartRange, setChartRange] = useState("1m");
  const [optionTypeFilter, setOptionTypeFilter] = useState("All");
  const [expirationFilter, setExpirationFilter] = useState("All");
  const [minStrikeFilter, setMinStrikeFilter] = useState("");
  const [maxStrikeFilter, setMaxStrikeFilter] = useState("");
  const [isOrderModalOpen, setOrderModalOpen] = useState(false);
  const id = Number(marketAssetId);

  const {
    data: asset,
    isLoading: assetLoading,
    error: assetError,
  } = marketDataApi.endpoints.getMarketAssetById.useQuery(id, { skip: !Number.isFinite(id) || id <= 0 });

  const {
    data: recommendations = [],
    isLoading: recLoading,
    error: recError,
  } = recommendationsApi.endpoints.getRecommendationsByMarketAssetId.useQuery(id, {
    skip: !Number.isFinite(id) || id <= 0,
  });
  const {
    data: marketHistory = [],
    isLoading: historyLoading,
    error: historyError,
  } = marketDataApi.endpoints.getMarketAssetHistory.useQuery(
    { marketAssetId: id, range: chartRange },
    { skip: !Number.isFinite(id) || id <= 0 }
  );
  const { data: positions = [], isLoading: positionsLoading, error: positionsError } =
    tradingApi.endpoints.getPositions.useQuery();

  const sorted = useMemo(() => {
    return [...recommendations].sort(
      (a, b) => new Date(b.dateGenerated).getTime() - new Date(a.dateGenerated).getTime()
    );
  }, [recommendations]);
  const latestRecommendation = sorted[0] || null;
  const chartSeries = useMemo(() => {
    const normalized = (marketHistory || []).map((point) => ({
      timestamp: point.timestamp ?? point.Timestamp,
      price: Number(point.price ?? point.Price ?? 0),
      volume: Number(point.volume ?? point.Volume ?? 0),
    }));
    return downsampleSeries(
      normalized
        .filter((point) => point.timestamp && Number.isFinite(point.price))
        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
    );
  }, [marketHistory]);
  const chartPerformance = useMemo(() => {
    if (!chartSeries.length) {
      return null;
    }

    const first = chartSeries[0];
    const last = chartSeries[chartSeries.length - 1];
    const change = last.price - first.price;
    const changePct = first.price ? (change / first.price) * 100 : 0;
    return { change, changePct };
  }, [chartSeries]);

  const optionsRows = useMemo(() => {
    const base = Number(asset?.currentPrice || 100);
    const expirations = [7, 14, 30, 60].map((days) => {
      const date = new Date();
      date.setDate(date.getDate() + days);
      return date.toISOString().slice(0, 10);
    });
    const multipliers = [0.8, 0.85, 0.9, 0.95, 1, 1.05, 1.1, 1.15, 1.2];
    const rows = [];
    let rowId = 1;

    expirations.forEach((expiration, expIdx) => {
      multipliers.forEach((m) => {
        const strike = Number((base * m).toFixed(2));
        const distance = Math.abs(m - 1);
        const callMid = Number((Math.max(base - strike, 0) + base * (0.045 + distance * 1.5)).toFixed(2));
        const putMid = Number((Math.max(strike - base, 0) + base * (0.04 + distance * 1.4)).toFixed(2));
        const ivBase = 22 + Math.round(distance * 120);
        const openInterestBase = Math.max(50, Math.round((1 - distance) * 1200));

        rows.push({
          id: rowId++,
          optionType: "Call",
          strike,
          expiration,
          bid: Number((callMid * 0.97).toFixed(2)),
          ask: Number((callMid * 1.03).toFixed(2)),
          iv: `${ivBase + expIdx}%`,
          openInterest: openInterestBase + expIdx * 120,
        });
        rows.push({
          id: rowId++,
          optionType: "Put",
          strike,
          expiration,
          bid: Number((putMid * 0.97).toFixed(2)),
          ask: Number((putMid * 1.03).toFixed(2)),
          iv: `${ivBase + expIdx + 1}%`,
          openInterest: openInterestBase - 20 + expIdx * 110,
        });
      });
    });

    return rows;
  }, [asset?.currentPrice]);

  const expirationOptions = useMemo(
    () => [...new Set(optionsRows.map((row) => row.expiration))],
    [optionsRows]
  );

  const filteredOptionsRows = useMemo(() => {
    return optionsRows.filter((row) => {
      if (optionTypeFilter !== "All" && row.optionType !== optionTypeFilter) return false;
      if (expirationFilter !== "All" && row.expiration !== expirationFilter) return false;
      if (minStrikeFilter !== "" && row.strike < Number(minStrikeFilter)) return false;
      if (maxStrikeFilter !== "" && row.strike > Number(maxStrikeFilter)) return false;
      return true;
    });
  }, [optionsRows, optionTypeFilter, expirationFilter, minStrikeFilter, maxStrikeFilter]);

  const optionColumns = useMemo(
    () => [
      { field: "optionType", headerName: "Type", valueGetter: (row) => row.optionType },
      {
        field: "strike",
        headerName: "Strike",
        align: "right",
        valueGetter: (row) => Number(row.strike || 0),
        valueFormatter: (value) => money(value),
      },
      { field: "expiration", headerName: "Expiration", valueGetter: (row) => row.expiration },
      {
        field: "bid",
        headerName: "Bid",
        align: "right",
        valueGetter: (row) => Number(row.bid || 0),
        valueFormatter: (value) => money(value),
      },
      {
        field: "ask",
        headerName: "Ask",
        align: "right",
        valueGetter: (row) => Number(row.ask || 0),
        valueFormatter: (value) => money(value),
      },
      { field: "iv", headerName: "IV", valueGetter: (row) => row.iv, align: "right" },
      { field: "openInterest", headerName: "Open Interest", valueGetter: (row) => row.openInterest, align: "right" },
    ],
    []
  );

  const assetPositions = useMemo(
    () => positions.filter((position) => Number(position.marketAssetId) === id),
    [positions, id]
  );

  const positionColumns = useMemo(
    () => [
      { field: "positionId", headerName: "Position Id", valueGetter: (row) => row.positionId },
      {
        field: "quantity",
        headerName: "Quantity",
        align: "right",
        valueGetter: (row) => Number(row.quantity || 0),
      },
      {
        field: "averageCostBasis",
        headerName: "Avg Cost",
        align: "right",
        valueGetter: (row) => Number(row.averageCostBasis || 0),
        valueFormatter: (value) => money(value),
      },
      {
        field: "marketValue",
        headerName: "Market Value",
        align: "right",
        valueGetter: (row) => Number(row.marketValue || 0),
        valueFormatter: (value) => money(value),
      },
      {
        field: "unrealizedPnl",
        headerName: "Unrealized P/L",
        align: "right",
        valueGetter: (row) => Number(row.unrealizedPnl || 0),
        renderCell: (row) => (
          <Typography color={Number(row.unrealizedPnl || 0) >= 0 ? "success.main" : "error.main"}>
            {money(row.unrealizedPnl)}
          </Typography>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        valueGetter: (row) => (row.isClosed ? "Closed" : "Open"),
        renderCell: (row) => (
          <Chip size="small" label={row.isClosed ? "Closed" : "Open"} color={row.isClosed ? "default" : "success"} />
        ),
      },
    ],
    []
  );

  if (!Number.isFinite(id) || id <= 0) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Invalid asset.
      </Alert>
    );
  }

  if (assetLoading || recLoading || positionsLoading) {
    return (
      <Box sx={{ width: "100%", mt: 2 }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 2, textAlign: "center" }}>
          Loading asset…
        </Typography>
      </Box>
    );
  }

  if (assetError || !asset) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Could not load this market asset.
      </Alert>
    );
  }

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/dashboard" underline="hover" color="inherit">
          Dashboard
        </Link>
        <Typography color="text.primary">{asset.symbol}</Typography>
      </Breadcrumbs>

      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/dashboard")} sx={{ mb: 2 }}>
        Back
      </Button>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar src={asset.imageUrl} alt={asset.symbol} sx={{ width: 56, height: 56 }}>
              {asset.symbol?.charAt(0)}
            </Avatar>
            <Box>
              <SectionHeader variant="h4" component="h1" sx={{ pb: 0.75 }}>
                {asset.name}
              </SectionHeader>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip label={asset.symbol} size="small" />
                <Typography variant="body2" color="text.secondary">
                  Rank #{asset.marketCapRank ?? "—"}
                </Typography>
              </Stack>
            </Box>
          </Stack>
          <Button
            variant="contained"
            onClick={() => setOrderModalOpen(true)}
          >
            Trade {asset.symbol}
          </Button>
        </Stack>
      </Paper>

      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, minWidth: 0 }}>
        <Box sx={{ width: { xs: "100%", md: 260 }, flexShrink: 0 }}>
          <Paper variant="outlined" sx={{ p: 1 }}>
            <Tabs
              orientation={window.innerWidth >= 900 ? "vertical" : "horizontal"}
              value={section}
              onChange={(_, value) => setSection(value)}
              variant="scrollable"
            >
              <Tab label="Overview" />
              <Tab label="AI Insights" />
              <Tab label="Options" />
              <Tab label="Positions" />
            </Tabs>
          </Paper>
        </Box>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          {section === 0 ? (
            <Stack spacing={2}>
              <SectionHeader variant="h5" component="h2" sx={{ pb: 0.75 }}>
                Overview
              </SectionHeader>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <AccentCard variant="outlined">
                    <CardContent>
                      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1.5 }}>
                        <SectionHeader variant="h6" component="h3">
                          Chart
                        </SectionHeader>
                        <Stack alignItems="flex-end" spacing={1}>
                          <ButtonGroup size="small" variant="outlined" aria-label="Chart scale">
                            {chartRanges.map((r) => (
                              <Button
                                key={r.value}
                                variant={chartRange === r.value ? "contained" : "outlined"}
                                onClick={() => setChartRange(r.value)}
                              >
                                {r.label}
                              </Button>
                            ))}
                          </ButtonGroup>
                          {chartPerformance ? (
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 700 }}
                              color={chartPerformance.change >= 0 ? "success.main" : "error.main"}
                            >
                              {signedMoney(chartPerformance.change)} ({chartPerformance.changePct >= 0 ? "+" : ""}
                              {chartPerformance.changePct.toFixed(2)}%)
                            </Typography>
                          ) : null}
                        </Stack>
                      </Stack>
                      {historyLoading ? <LinearProgress sx={{ mb: 1 }} /> : null}
                      {historyError ? (
                        <Alert severity="warning">Could not load market history for this time range.</Alert>
                      ) : null}
                      {chartSeries.length === 0 ? (
                        <Typography color="text.secondary">No historical data available for the selected range.</Typography>
                      ) : (
                        <Box component="svg" viewBox="0 0 700 240" sx={{ width: "100%", height: 220 }}>
                          {(() => {
                            const minPrice = Math.min(...chartSeries.map((point) => point.price));
                            const maxPrice = Math.max(...chartSeries.map((point) => point.price));
                            const range = Math.max(0.00000001, maxPrice - minPrice);
                            const xLeft = 48;
                            const xRight = 680;
                            const yTop = 18;
                            const yBottom = 196;
                            const width = xRight - xLeft;
                            const height = yBottom - yTop;
                            const toX = (index) =>
                              xLeft + (chartSeries.length <= 1 ? 0 : (index / (chartSeries.length - 1)) * width);
                            const toY = (price) => yBottom - ((price - minPrice) / range) * height;

                            const linePoints = chartSeries
                              .map((point, index) => `${toX(index)},${toY(point.price)}`)
                              .join(" ");

                            const yTicks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => {
                              const price = maxPrice - range * ratio;
                              const y = yTop + height * ratio;
                              return { price, y };
                            });

                            const xTicks = [0, 0.5, 1].map((ratio) => {
                              const index = Math.min(
                                chartSeries.length - 1,
                                Math.max(0, Math.round((chartSeries.length - 1) * ratio))
                              );
                              const x = toX(index);
                              const timestamp = new Date(chartSeries[index].timestamp);
                              const label =
                                chartRange === "1d"
                                  ? timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                                  : timestamp.toLocaleDateString([], { month: "short", day: "numeric" });
                              return { x, label };
                            });

                            return (
                              <>
                                {yTicks.map((tick) => (
                                  <g key={tick.y}>
                                    <line x1={xLeft} y1={tick.y} x2={xRight} y2={tick.y} stroke="#e0e0e0" strokeWidth="1" />
                                    <text x={xLeft - 8} y={tick.y + 4} textAnchor="end" fontSize="11" fill="#6b7280">
                                      {money(tick.price)}
                                    </text>
                                  </g>
                                ))}

                                <polyline fill="none" stroke="#1976d2" strokeWidth="2.4" points={linePoints} />

                                {xTicks.map((tick) => (
                                  <text key={tick.x} x={tick.x} y={220} textAnchor="middle" fontSize="11" fill="#6b7280">
                                    {tick.label}
                                  </text>
                                ))}
                              </>
                            );
                          })()}
                        </Box>
                      )}
                    </CardContent>
                  </AccentCard>
                </Grid>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <AccentCard variant="outlined">
                    <CardContent>
                      <SectionHeader variant="h6" component="h3" sx={{ mb: 1.5 }}>
                        Detailed Quote
                      </SectionHeader>
                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between"><Typography color="text.secondary">Price</Typography><Typography>{money(asset.currentPrice)}</Typography></Stack>
                        <Stack direction="row" justifyContent="space-between"><Typography color="text.secondary">24h Change</Typography><Typography color={Number(asset.priceChangePercentage24h || 0) >= 0 ? "success.main" : "error.main"}>{pct(asset.priceChangePercentage24h)}</Typography></Stack>
                        <Stack direction="row" justifyContent="space-between"><Typography color="text.secondary">Market Cap</Typography><Typography>{compactMoney(asset.marketCap)}</Typography></Stack>
                        <Stack direction="row" justifyContent="space-between"><Typography color="text.secondary">Volume</Typography><Typography>{compactMoney(asset.totalVolume)}</Typography></Stack>
                        <Stack direction="row" justifyContent="space-between"><Typography color="text.secondary">Exchange</Typography><Typography>{asset.exchangeSymbol || "—"}</Typography></Stack>
                      </Stack>
                    </CardContent>
                  </AccentCard>
                </Grid>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <AccentCard variant="outlined">
                    <CardContent>
                      <SectionHeader variant="h6" component="h3" sx={{ mb: 1.5 }}>
                        Performance
                      </SectionHeader>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Chip label={`24h ${pct(asset.priceChangePercentage24h)}`} color={Number(asset.priceChangePercentage24h || 0) >= 0 ? "success" : "error"} />
                        <Chip label={`MCap Rank #${asset.marketCapRank ?? "—"}`} variant="outlined" />
                        <Chip label={`Volume ${compactMoney(asset.totalVolume)}`} variant="outlined" />
                      </Stack>
                    </CardContent>
                  </AccentCard>
                </Grid>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <AccentCard variant="outlined">
                    <CardContent>
                      <SectionHeader variant="h6" component="h3" sx={{ mb: 1.5 }}>
                        AI Overview
                      </SectionHeader>
                      {latestRecommendation ? (
                        <Stack spacing={1}>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip label={latestRecommendation.overallRecommendation?.recommendationLevel || "—"} color="primary" variant="outlined" />
                            <Chip label={`Trend ${latestRecommendation.trendAnalysis?.trend || "—"}`} />
                            <Chip label={`Risk ${latestRecommendation.riskAssessment?.level || "—"}`} />
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            {latestRecommendation.overallRecommendation?.thesis || "No AI thesis available yet."}
                          </Typography>
                          <Button size="small" onClick={() => setSection(1)}>View full AI insights</Button>
                        </Stack>
                      ) : (
                        <Typography color="text.secondary">No AI recommendation available yet.</Typography>
                      )}
                    </CardContent>
                  </AccentCard>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <AccentCard variant="outlined">
                    <CardContent>
                      <SectionHeader variant="h6" component="h3" sx={{ mb: 1.5 }}>
                        News
                      </SectionHeader>
                      {latestRecommendation?.sentimentAnalysis?.keySentimentDrivers?.length ? (
                        <Stack spacing={0.75}>
                          {latestRecommendation.sentimentAnalysis.keySentimentDrivers.slice(0, 6).map((item, idx) => (
                            <Typography key={`${item}-${idx}`} variant="body2">- {item}</Typography>
                          ))}
                        </Stack>
                      ) : (
                        <Typography color="text.secondary">No recent AI-tagged news drivers available.</Typography>
                      )}
                    </CardContent>
                  </AccentCard>
                </Grid>
              </Grid>
            </Stack>
          ) : null}

          {section === 1 ? (
            <Stack spacing={2}>
              <SectionHeader variant="h5" component="h2" sx={{ pb: 0.75 }}>
                AI Insights
              </SectionHeader>
              {recError ? (
                <Alert severity="warning">Recommendations could not be loaded.</Alert>
              ) : null}
              {sorted.length === 0 ? (
                <Paper sx={{ p: 3 }}>
                  <Typography color="text.secondary">No AI recommendations on record for this asset yet.</Typography>
                </Paper>
              ) : (
                <Stack spacing={3}>
                  {sorted.map((rec) => (
                    <Paper key={rec.aiAssetRecommendationId} variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Recommendation #{rec.aiAssetRecommendationId}
                      </Typography>
                      <RecommendationSections recommendation={rec} />
                    </Paper>
                  ))}
                </Stack>
              )}
            </Stack>
          ) : null}

          {section === 2 ? (
            <Stack spacing={2}>
              <SectionHeader variant="h5" component="h2" sx={{ pb: 0.75 }}>
                Options
              </SectionHeader>
              <Alert severity="info">
                Options chain is shown with preview data until a live options feed is connected.
              </Alert>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="options-type-label">Type</InputLabel>
                    <Select
                      labelId="options-type-label"
                      value={optionTypeFilter}
                      label="Type"
                      onChange={(e) => setOptionTypeFilter(e.target.value)}
                    >
                      <MenuItem value="All">All</MenuItem>
                      <MenuItem value="Call">Calls</MenuItem>
                      <MenuItem value="Put">Puts</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="options-exp-label">Expiration</InputLabel>
                    <Select
                      labelId="options-exp-label"
                      value={expirationFilter}
                      label="Expiration"
                      onChange={(e) => setExpirationFilter(e.target.value)}
                    >
                      <MenuItem value="All">All</MenuItem>
                      {expirationOptions.map((value) => (
                        <MenuItem key={value} value={value}>
                          {value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label="Min Strike"
                    type="number"
                    value={minStrikeFilter}
                    onChange={(e) => setMinStrikeFilter(e.target.value)}
                  />
                  <TextField
                    size="small"
                    label="Max Strike"
                    type="number"
                    value={maxStrikeFilter}
                    onChange={(e) => setMaxStrikeFilter(e.target.value)}
                  />
                </Stack>
              </Paper>
              <CommonDataGrid
                rows={filteredOptionsRows}
                columns={optionColumns}
                getRowId={(row) => row.id}
                initialSortField="expiration"
                initialSortDirection="asc"
                filterPlaceholder="Filter options by expiration, type, strike, IV..."
              />
            </Stack>
          ) : null}

          {section === 3 ? (
            <Stack spacing={2}>
              <SectionHeader variant="h5" component="h2" sx={{ pb: 0.75 }}>
                Positions
              </SectionHeader>
              {positionsError ? <Alert severity="error">Failed to load positions.</Alert> : null}
              {assetPositions.length === 0 ? (
                <Paper sx={{ p: 3 }}>
                  <Typography color="text.secondary">
                    You do not currently hold a position in {asset.symbol}.
                  </Typography>
                </Paper>
              ) : (
                <>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <AccentCard variant="outlined">
                        <CardContent>
                          <Typography variant="body2" color="text.secondary">Total Quantity</Typography>
                          <Typography variant="h6">{assetPositions.reduce((sum, p) => sum + Number(p.quantity || 0), 0)}</Typography>
                        </CardContent>
                      </AccentCard>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <AccentCard variant="outlined">
                        <CardContent>
                          <Typography variant="body2" color="text.secondary">Total Market Value</Typography>
                          <Typography variant="h6">{money(assetPositions.reduce((sum, p) => sum + Number(p.marketValue || 0), 0))}</Typography>
                        </CardContent>
                      </AccentCard>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <AccentCard variant="outlined">
                        <CardContent>
                          <Typography variant="body2" color="text.secondary">Total Unrealized P/L</Typography>
                          <Typography variant="h6" color={assetPositions.reduce((sum, p) => sum + Number(p.unrealizedPnl || 0), 0) >= 0 ? "success.main" : "error.main"}>
                            {money(assetPositions.reduce((sum, p) => sum + Number(p.unrealizedPnl || 0), 0))}
                          </Typography>
                        </CardContent>
                      </AccentCard>
                    </Grid>
                  </Grid>
                  <CommonDataGrid
                    rows={assetPositions}
                    columns={positionColumns}
                    getRowId={(row) => row.positionId}
                    initialSortField="marketValue"
                    initialSortDirection="desc"
                    filterPlaceholder="Filter positions by status or value..."
                  />
                </>
              )}
            </Stack>
          ) : null}
        </Box>
      </Box>

      <Box sx={{ position: "fixed", right: 24, bottom: 24, zIndex: 1200 }}>
        <Button
          size="large"
          variant="contained"
          onClick={() => setOrderModalOpen(true)}
        >
          Trade {asset.symbol}
        </Button>
      </Box>
      <OrderModal
        open={isOrderModalOpen}
        onClose={() => setOrderModalOpen(false)}
        defaultSymbol={asset.symbol}
        symbolLocked
        onOrderPlaced={() => setOrderModalOpen(false)}
      />
    </Box>
  );
};

export default AssetDetailPage;
